/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useLocation } from 'react-router-dom'
import Select from 'react-select'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo'
import setGlobalLoader from '../../redux/actions/loaderActions'
import { organizationSignup } from '../../redux/actions/apiActions'
import { statesAndCities } from '../../../src/utils/indianStatesCities'
import { CircularProgress, FormControl } from '@mui/material'

const TUTORIAL_VIDEO_URL = 'https://youtu.be/HJ7cVuct4Ik?si=FEikPbTZTSkJccwc'
const HELP_PLAYLIST_URL = 'https://youtube.com/playlist?list=PLaDDvncthuYxxu57utCRGKdTnL65rBDZj&si=JAgHrLCIaizF7jmg'

const REFERRAL_KEY = 'drivolo_referral'
const REFERRAL_TTL_MS = 15 * 24 * 60 * 60 * 1000 // 15 days

function saveReferral(ref) {
  localStorage.setItem(
    REFERRAL_KEY,
    JSON.stringify({ value: ref, expiry: Date.now() + REFERRAL_TTL_MS })
  )
}

function getReferral() {
  try {
    const raw = localStorage.getItem(REFERRAL_KEY)
    if (!raw) return null
    const { value, expiry } = JSON.parse(raw)
    if (Date.now() > expiry) {
      localStorage.removeItem(REFERRAL_KEY)
      return null
    }
    return value
  } catch {
    return null
  }
}

const ATTENDANCE_MODES = {
  KM: 'KM',
  MINUTES: 'MINUTES'
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  proprietorName: yup.string().required('Proprietor Name is required'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits'),
  email: yup
    .string()
    .email('Invalid email address')
    .lowercase(),
  address: yup.string().required('Address is required'),
  pincode: yup
    .number()
    .required('Pincode is required')
    .min(100000, 'Pincode must be 6 digits')
    .max(999999, 'Pincode must be 6 digits'),
  state: yup.string().required('State is required'),
  city: yup.string().required('City is required'),
  license: yup.string(),
  bankAccountHolderName: yup.string(),
  bankIFSCCode: yup
    .string()
    .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code'),
  bankAccountNumber: yup
    .string()
    .matches(/^[0-9]{9,18}$/, 'Account number must be 9-18 digits'),
  attendanceMode: yup.string().oneOf([ATTENDANCE_MODES.KM, ATTENDANCE_MODES.MINUTES]),
  agentPhoneNumber: yup
    .string()
    .test('len', 'Agent phone number must be 10 digits', val => !val || /^\d{10}$/.test(val)),
})

const Form = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const [selectedState, setSelectedState] = useState('');
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [cityOptions, setCityOptions] = useState([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ref = params.get('ref')
    if (ref) {
      saveReferral(ref)
    }
  }, [location.search])

  useEffect(() => {
    setStateOptions(statesAndCities.map(item => ({ "label": item.state , "value": item.state })));
    if (selectedState) {
      const cities = statesAndCities.find(state => state.state.toLowerCase() === selectedState?.value.toLowerCase()).cities;
      cities.sort((a, b) => a.localeCompare(b));
      setCityOptions(cities.map(city => ({  "label": city , "value": city })));
    }
  }, [selectedState]);

  const handleOnSubmit = async (values) => {
    console.log("values: ", values)
    if (values.state === '') {
      toast.error('Please select a State.')
      return
    }
    if (values.city === '') {
      toast.error('Please select a City.')
      return
    }
    try {
      const referredByOrgId = getReferral()
      const payload = { ...values, license: values.license === '' ? null : values.license }
      if (referredByOrgId) payload.referredByOrgId = referredByOrgId
      if (!values.email) delete payload.email
      if (!values.agentPhoneNumber) delete payload.agentPhoneNumber
      await organizationSignup(payload)
      toast.success('Details saved successfully. We will contact you soon.')
      dispatch(setGlobalLoader(false))
      setShowSuccessMessage(true);
      setTimeout(() => {
        navigate('/login')
      }, 10000);
    } catch (error) {
      dispatch(setGlobalLoader(false))
      console.log('create Organization error', error)
      toast.error(
        error.response.data.msg ??
        'some error occured while raising the request.'
      )
    }
  }
  return (
    <Box>
      <Formik
        initialValues={{
          phoneNumber: '',
          name: '',
          proprietorName: '',
          email: '',
          license: '',
          pincode: '',
          address: '',
          city: '',
          state: '',
          bankAccountHolderName: '',
          bankIFSCCode: '',
          bankAccountNumber: '',
          attendanceMode: ATTENDANCE_MODES.KM,
          whatsappCommunicatioun: false,
          agentPhoneNumber: '',
        }}
        validationSchema={validationSchema}
        onSubmit={values => handleOnSubmit(values)}
      >
        {formikProps => {
          const {
            values,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
            isSubmitting,
          } = formikProps
          return (
            <>
              <Box marginBottom={4}>
                <Typography
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 'medium',
                  }}
                  gutterBottom
                  color={'text.secondary'}
                >
                  Signup
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  Welcome
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayCircleOutlineIcon />}
                    onClick={() => window.open(TUTORIAL_VIDEO_URL, '_blank', 'noopener,noreferrer')}
                  >
                    Watch Tutorial
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OndemandVideoIcon />}
                    onClick={() => window.open(HELP_PLAYLIST_URL, '_blank', 'noopener,noreferrer')}
                  >
                    Help Videos
                  </Button>
                </Box>
              </Box>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                  <>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Driving School Name
                      </Typography>
                      <TextField
                        label="Name *"
                        variant="outlined"
                        name={'name'}
                        fullWidth
                        value={values.name}
                        onChange={e => setFieldValue('name', e.target.value)}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Proprietor Name
                      </Typography>
                      <TextField
                        label="Proprietor Name *"
                        variant="outlined"
                        name={'proprietorName'}
                        fullWidth
                        value={values.proprietorName}
                        onChange={e => setFieldValue('proprietorName', e.target.value)}
                        error={touched.proprietorName && Boolean(errors.proprietorName)}
                        helperText={touched.proprietorName && errors.proprietorName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Phone Number
                      </Typography>
                      <TextField
                        label="Phone Number *"
                        variant="outlined"
                        name={'phoneNumber'}
                        fullWidth
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        value={values.phoneNumber}
                        onChange={e =>
                          e.target.value.toString().length > 10
                            ? setFieldValue(
                              'phoneNumber',
                              e.target.value.substring(0, 10)
                            )
                            : setFieldValue('phoneNumber', e.target.value)
                        }
                        error={
                          touched.phoneNumber && Boolean(errors.phoneNumber)
                        }
                        helperText={touched.phoneNumber && errors.phoneNumber}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Email
                      </Typography>
                      <TextField
                        label="Email"
                        variant="outlined"
                        name={'email'}
                        fullWidth
                        value={values.email}
                        onChange={e => setFieldValue('email', e.target.value)}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        License Number
                      </Typography>
                      <TextField
                        label="License Number"
                        variant="outlined"
                        name={'license'}
                        type="text"
                        fullWidth
                        value={values.license}
                        onChange={e =>
                          setFieldValue('license', e.target.value.toString())
                        }
                        error={touched.license && Boolean(errors.license)}
                        helperText={touched.license && errors.license}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Attendance Recording Mode
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                        Choose how attendance will be recorded for students
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          row
                          value={values.attendanceMode}
                          onChange={(e) => setFieldValue('attendanceMode', e.target.value)}
                        >
                          <FormControlLabel
                            value={ATTENDANCE_MODES.KM}
                            control={<Radio />}
                            label="Kilometers (KM)"
                          />
                          <FormControlLabel
                            value={ATTENDANCE_MODES.MINUTES}
                            control={<Radio />}
                            label="Minutes (Min)"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Address
                      </Typography>
                      <TextField
                        label="Address *"
                        variant="outlined"
                        name={'address'}
                        fullWidth
                        value={values.address}
                        onChange={e => setFieldValue('address', e.target.value)}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        State
                      </Typography>

                      <FormControl
                        fullWidth
                        error={touched.state && Boolean(errors.state)}
                        helperText={touched.state && errors.state}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 })
                          }}
                          placeholder="Select State *"
                          value={selectedState}
                          options={stateOptions}
                          fullWidth
                          onChange={(e) => {
                            setSelectedState(e)
                            setFieldValue("state", e.value)
                            setSelectedCity(null)
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        City
                      </Typography>
                      <FormControl
                        fullWidth
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && Boolean(errors.city)}
                      >
                        <Select
                          styles={{
                            menu: provided => ({ ...provided, zIndex: 9999 })
                          }}
                          placeholder="Select City *"
                          value={selectedCity}
                          options={cityOptions}
                          fullWidth
                          isDisabled={!selectedState}
                          onChange={(e) => {
                            setSelectedCity(e)
                            setFieldValue("city", e.value)
                          }}
                        />

                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Pincode
                      </Typography>
                      <TextField
                        label="Pincode *"
                        variant="outlined"
                        name={'pincode'}
                        fullWidth
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        value={values.pincode}
                        onChange={e =>
                          e.target.value.toString().length > 6
                            ? setFieldValue('pincode', e.target.value.substring(0, 6))
                            : setFieldValue('pincode', e.target.value)
                        }
                        error={touched.pincode && Boolean(errors.pincode)}
                        helperText={touched.pincode && errors.pincode}
                      />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Agent Phone Number (Optional)
                      </Typography>
                      <TextField
                        label="Agent Phone Number"
                        variant="outlined"
                        name={'agentPhoneNumber'}
                        fullWidth
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={values.agentPhoneNumber}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').substring(0, 10)
                          setFieldValue('agentPhoneNumber', val)
                        }}
                        error={touched.agentPhoneNumber && Boolean(errors.agentPhoneNumber)}
                        helperText={(touched.agentPhoneNumber && errors.agentPhoneNumber) || 'If you were referred by an agent, enter their phone number'}
                      />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Agent Phone Number (Optional)
                      </Typography>
                      <TextField
                        label="Agent Phone Number"
                        variant="outlined"
                        name={'agentPhoneNumber'}
                        fullWidth
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={values.agentPhoneNumber}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').substring(0, 10)
                          setFieldValue('agentPhoneNumber', val)
                        }}
                        error={touched.agentPhoneNumber && Boolean(errors.agentPhoneNumber)}
                        helperText={(touched.agentPhoneNumber && errors.agentPhoneNumber) || 'If you were referred by an agent, enter their phone number'}
                      />
                    </Grid>
                    {/* <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Bank Account Holder Name
                      </Typography>
                      <TextField
                        label="Bank Account Holder Name"
                        variant="outlined"
                        name={'bankAccountHolderName'}
                        fullWidth
                        value={values.bankAccountHolderName}
                        onChange={e => setFieldValue('bankAccountHolderName', e.target.value)}
                        error={touched.bankAccountHolderName && Boolean(errors.bankAccountHolderName)}
                        helperText={touched.bankAccountHolderName && errors.bankAccountHolderName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Bank IFSC Code
                      </Typography>
                      <TextField
                        label="Bank IFSC Code"
                        variant="outlined"
                        name={'bankIFSCCode'}
                        fullWidth
                        value={values.bankIFSCCode}
                        onChange={e => setFieldValue('bankIFSCCode', e.target.value.toUpperCase())}
                        error={touched.bankIFSCCode && Boolean(errors.bankIFSCCode)}
                        helperText={touched.bankIFSCCode && errors.bankIFSCCode}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant={'subtitle2'}
                        sx={{ marginBottom: 1 }}
                      >
                        Bank Account Number
                      </Typography>
                      <TextField
                        label="Bank Account Number"
                        variant="outlined"
                        name={'bankAccountNumber'}
                        fullWidth
                        type="number"
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        value={values.bankAccountNumber}
                        onChange={e =>
                          e.target.value.toString().length > 18
                            ? setFieldValue('bankAccountNumber', e.target.value.substring(0, 18))
                            : setFieldValue('bankAccountNumber', e.target.value)
                        }
                        error={touched.bankAccountNumber && Boolean(errors.bankAccountNumber)}
                        helperText={touched.bankAccountNumber && errors.bankAccountNumber}
                      />
                    </Grid> */}
                    <Grid item xs={12}>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        flex-direction="column"
                        alignItems="center"
                        marginTop={2}
                      >
                        <Checkbox
                          checked={values.transactionalCommunication}
                          onChange={e =>
                            setFieldValue(
                              'transactionalCommunication',
                              e.target.checked
                            )
                          }
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 'light',
                          }}
                        >
                          Receive important notifications via email and sms.
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        flex-direction="column"
                        alignItems="center"
                        marginTop={2}
                      >
                        <Checkbox
                          checked={values.marketingCommunication}
                          onChange={e =>
                            setFieldValue(
                              'marketingCommunication',
                              e.target.checked
                            )
                          }
                        />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 'light',
                          }}
                        >
                          Receive marketing and service related notifications via email and sms.
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                  <Grid item container xs={12}>
                    <Button
                      id="send_otp"
                      size={'large'}
                      variant={'contained'}
                      type={'submit'}
                      disabled={isSubmitting || showSuccessMessage}
                    >
                      {isSubmitting ? <CircularProgress /> : 'Confirm'}
                    </Button>
                  </Grid>
                  {showSuccessMessage &&
                    <Grid item container xs={12}>
                    <Typography
                          variant={'h6'}
                          sx={{ marginBottom: 1 , color : "blue"}}
                        >
                          We have received your details. We will contact you soon.
                          You can also call/whatsapp us at +91 8143869980.
                      </Typography>
                  </Grid>
                  }
                </Grid>
              </form >
            </>
          )
        }}
      </Formik>
    </Box >
  )
}

export default Form
